import {makeAutoObservable, observable, runInAction} from 'mobx'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {Checklist, ChecklistItem, Hub} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {showDeleteConfirm} from '@components/common/base/notifications'
import {TextAreaApi} from '@components/common/base/textarea/types'
import {createUseStore} from '@containers/hooks'
import {Task} from '@extensions/task'
import {ChecklistsStore} from '../../checklists-store'

interface State {
  addingItem: boolean
  editing: boolean
  unsavedTitle?: string
  unsavedAddItem?: string

  preventOnBlur?: boolean

  clearAddItemCallback?: () => void
}

const completed = (s: ChecklistItem): boolean => !!s?.isCompleted

export class ChecklistStore {
  state: State

  private task: Hub
  private list?: Checklist
  private addElement?: HTMLTextAreaElement

  constructor(private checklistsStore: ChecklistsStore) {
    this.state = {
      addingItem: false,
      editing: false,
      preventOnBlur: false
    }
    this.list = {} as Checklist

    makeAutoObservable(
      this,
      {
        state: observable.deep
      },
      {autoBind: true}
    )
  }

  getCompletedListCount(): number {
    return (
      this.list?.items?.filter(isChecklistItem).filter(completed).length ?? 0
    )
  }

  getListCount(): number {
    return this.list?.items?.filter(isChecklistItem).length ?? 0
  }

  sync(task: Hub, list: Checklist): void {
    this.task = task
    this.list = list
  }

  startAddItem(): void {
    const isTaskTemplate = Task.isTaskTemplate(this.task)
    if (isTaskTemplate || PermissionsChecker.checkEditSubtasks(this.task)) {
      this.state.addingItem = true
      this.checklistsStore.updateEditInProgress(true)
    }
  }

  submitAddItem(api: TextAreaApi, onBlur: boolean): void {
    if (this.state.preventOnBlur && onBlur) {
      this.state.preventOnBlur = false
      return
    }

    api.baseElement && (api.baseElement.value = '')

    const text = this.state.unsavedAddItem
    if (text) {
      text
        .split('\n')
        .filter(Util.isDefined)
        .forEach(t => this.list && Task.addSubtask(this.task, this.list.id, t))

      this.state.unsavedAddItem = undefined
      this.state.clearAddItemCallback = () => {
        api.baseElement && (api.baseElement.value = '')
        this.afterCheckListItemAdded()
        this.state.clearAddItemCallback = undefined
      }
    }

    if (!text || onBlur) {
      this.state.addingItem = false
      this.checklistsStore.updateEditInProgress(false)
    }
  }

  cancelAddItem(): void {
    this.state.addingItem = false
    this.checklistsStore.updateEditInProgress(false)
  }

  onChangeAddItem(api: TextAreaApi): void {
    this.state.unsavedAddItem = api.text
    this.addElement = api.baseElement
  }

  private afterCheckListItemAdded(): void {
    if (!this.list) {
      return
    }
    const id = `add-checklist-item-${this.list.id}`
    const el = document.getElementById(id)
    el?.scrollIntoView({block: 'nearest'})
  }

  startEditTitle(): void {
    const isTaskTemplate = Task.isTaskTemplate(this.task)
    if (isTaskTemplate || PermissionsChecker.checkEditSubtasks(this.task)) {
      this.state.editing = true
      this.checklistsStore.updateEditInProgress(true)
    }
  }

  onChangeTitle(api: TextAreaApi): void {
    this.state.unsavedTitle = api.text
  }

  submitEditTitle(): void {
    if (!this.state.editing || !this.list) {
      return
    }

    void Tracking.send(TrackingEvent.taskEditSubtasks)
    const text = this.state.unsavedTitle

    if (text) {
      Task.renameChecklist(this.task, this.list.id, text)
    } else if (text === '') {
      void this.delete()
    }
    this.state.unsavedTitle = undefined
    this.state.editing = false
    this.checklistsStore.updateEditInProgress(false)
  }

  cancelEditTitle(): void {
    this.state.unsavedTitle = undefined
    this.state.editing = false
    this.checklistsStore.updateEditInProgress(false)
  }

  blurEditTitle(): void {
    if (!this.state.editing) {
      return
    }

    const text = this.state.unsavedTitle
    if (text) {
      if (this.list?.title !== text) {
        this.state.unsavedTitle = text
      } else {
        this.state.unsavedTitle = undefined
      }
    }

    this.state.editing = false
    this.checklistsStore.updateEditInProgress(false)
  }

  async delete(): Promise<void> {
    if (
      await showDeleteConfirm({
        title: Lang.msg.sureDeleteChecklist,
        subtitle: Lang.msg.sureDeleteChecklistHint
      })
    ) {
      runInAction(() => {
        this.state.editing = false
        this.checklistsStore.updateEditInProgress(false)
        this.list && Task.deleteChecklist(this.task, this.list.id)
      })
    }
  }
}

export const [ChecklistContext, useChecklistStore] =
  createUseStore(ChecklistStore)

export const isChecklistItem = (item: ChecklistItem): boolean =>
  !!item?.id && !!item.title
